import { Campaign, FitRequirements, Notice, ScheduleItem, UserInDetails } from '@/hub-shared'
import onEnvironmentChange from '@/observables/environment.observable'
import type Environment from '@interfaces/environment.interface'
import LogService from '@services/log/log.service'
import MainService from '@services/main/main.service'
import type { AxiosResponse } from 'axios'
import { take } from 'rxjs/operators'

export default class HubCampaignsService extends MainService {
  url: string
  logService = new LogService()

  constructor(hubCampaignsUrl?: string) {
    super()
    if (!hubCampaignsUrl) {
      onEnvironmentChange.pipe(take(1)).subscribe((environment: Environment) => {
        this.url = environment?.squidApis?.hubCampaigns
      })
    } else {
      this.url = hubCampaignsUrl
    }
    this.logService.init('Hub Campaigns Service')
  }

  public async getCampaignQuantity() {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/campaigns/quantity-invitation/self`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get campaign quantity ',
          error,
        })
        throw error.response
      })
  }

  public async getListCampaignsByType(
    listType: string,
    socialNetwork: 'instagram' | 'twitter' | 'youtube' | 'tiktok' | '' = '',
    page = 1,
    limit = 6
  ) {
    const headers = await this.getHeaders()
    if (!listType) {
      throw new Error('listType is required in getListCampaignsByType method of HubCampaignsService.')
    }

    return this.http
      .get(`${this.url}/campaigns/${listType}/self?limit=${limit}&page=${page}${socialNetwork ? `&network=${socialNetwork}` : ''}`, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: `Error on get ${listType} campaigns `,
          error,
        })
        throw error?.response
      })
  }

  public async getCampaignDetail(
    campaignId: string,
    idSocialNetwork?: string
  ): Promise<{
    campaign: Campaign
    fitRequirements: FitRequirements
    users: UserInDetails[]
    hasVacancies: boolean
  }> {
    const headers = await this.getHeaders()
    if (!campaignId) {
      throw new Error('campaignId is required in getCampaignDetail method of HubCampaignsService.')
    }

    const urlCampaign = idSocialNetwork
      ? `${this.url}/campaign/detail/${campaignId}/${idSocialNetwork}`
      : `${this.url}/campaign/detail/${campaignId}`

    return this.http
      .get(`${urlCampaign}`, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get detail campaigns ',
          error,
        })
        return error?.response
      })
  }

  public async getSchedules(campaignId: string, squidId: string): Promise<Array<ScheduleItem>> {
    const headers = await this.getHeaders()

    if (!campaignId) {
      throw new Error('campaignId is required in getSchedules method of HubCampaignsService.')
    }

    if (!squidId) {
      throw new Error('squidId is required in getSchedules method of HubCampaignsService.')
    }

    return this.http
      .get(`${this.url}/campaign/${campaignId}/influencer/${squidId}/schedule`, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get schedules ',
          error,
        })
        throw error?.response
      })
  }

  public async getNotices(campaignId: string, limit = 10, page = 1): Promise<Array<Notice>> {
    const headers = await this.getHeaders()

    if (!campaignId) {
      throw new Error('campaignId is required in getNotices method of HubCampaignsService.')
    }

    return this.http
      .get(`${this.url}/notices/campaign/${campaignId}?limit=${limit}&page=${page}`, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get notices ',
          error,
        })
        throw error?.response
      })
  }

  public async getSurveyAnswered(campaignId: string, idNetwork: string) {
    const headers = await this.getHeaders()

    if (!campaignId) {
      throw new Error('campaignId is required in getSurveyAnswered method of HubCampaignsService.')
    }

    if (!idNetwork) {
      throw new Error('idNetwork is required in getSurveyAnswered method of HubCampaignsService.')
    }

    return this.http
      .get(`${this.url}/campaign/${campaignId}/research/${idNetwork}`, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get survey answered ',
          error,
        })
        throw error?.response
      })
  }

  public async postCampaignsAction(
    campaignId: string,
    profileId: string,
    action: 'accepted' | 'refused',
    motivation = '',
    opportunity?: boolean
  ) {
    const headers = await this.getHeaders()

    if (!profileId) {
      throw new Error('profileId is required in the postCampaignsAction method of HubCampaignsService.')
    }
    if (!campaignId) {
      throw new Error('campaignId is required in the postCampaignsAction method of HubCampaignsService.')
    }

    return this.http
      .post(
        `${this.url}/campaigns/${campaignId}/recruitment/${profileId}${opportunity ? '/opportunity' : ''}/${action}`,
        motivation?.length ? { motivation } : null,
        { headers }
      )
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: `Error on ${action === 'accepted' ? 'accept' : 'refuse'} campaign`,
          error,
        })
        throw error?.response
      })
  }
}
